import adminApi from './adminApi';

// ========== DTOs ==========

export interface McpStatsDTO {
  callsToday: number;
  calls7d: number;
  calls30d: number;
  uniqueUsers30d: number;
  errorRate30d: number;
  topTools: NameCountDTO[];
  topUsers: NameCountDTO[];
  topClients: NameCountDTO[];
}

export interface NameCountDTO {
  name: string;
  count: number;
}

export interface McpToolStatsDTO {
  toolName: string;
  totalCalls: number;
  avgDurationMs: number;
  errorRate: number;
  lastCalledAt: string | null;
}

export interface McpUserStatsDTO {
  userEmail: string;
  userId: number | null;
  totalCalls: number;
  lastActiveAt: string | null;
  mostUsedTool: string | null;
  errorRate: number;
}

export interface McpAnomalyDTO {
  anomalyType: string;
  severity: 'red' | 'orange' | 'yellow';
  description: string;
  affectedUserEmail: string | null;
  affectedToolName: string | null;
  affectedSessionId: string | null;
  count: number;
  lastSeenAt: string | null;
}

export interface McpCallDTO {
  id: number;
  userEmail: string | null;
  clientName: string | null;
  toolName: string;
  calledAt: string;
  durationMs: number | null;
  success: boolean;
  errorMessage: string | null;
  sessionId: string | null;
}

export interface McpCallFilters {
  userEmail?: string;
  toolName?: string;
  success?: boolean;
  page?: number;
  size?: number;
}

// ========== API functions ==========

/**
 * Fetches MCP summary statistics (call volumes, error rate, top-5 lists).
 */
export const getMcpStats = async (): Promise<McpStatsDTO> => {
  const response = await adminApi.get('/api/admin/mcp/stats');
  return response.data.data;
};

/**
 * Fetches per-tool MCP statistics.
 */
export const getMcpToolStats = async (): Promise<McpToolStatsDTO[]> => {
  const response = await adminApi.get('/api/admin/mcp/tools');
  return response.data.data;
};

/**
 * Fetches per-user MCP statistics.
 */
export const getMcpUserStats = async (): Promise<McpUserStatsDTO[]> => {
  const response = await adminApi.get('/api/admin/mcp/users');
  return response.data.data;
};

/**
 * Fetches detected anomalies from the MCP audit log.
 */
export const getMcpAnomalies = async (): Promise<McpAnomalyDTO[]> => {
  const response = await adminApi.get('/api/admin/mcp/anomalies');
  return response.data.data;
};

/**
 * Fetches a paginated, filtered raw MCP call log.
 */
export const getMcpCalls = async (filters: McpCallFilters = {}): Promise<McpCallDTO[]> => {
  const params: Record<string, string | number | boolean> = {};
  if (filters.userEmail) params.userEmail = filters.userEmail;
  if (filters.toolName) params.toolName = filters.toolName;
  if (filters.success !== undefined) params.success = filters.success;
  params.page = filters.page ?? 0;
  params.size = filters.size ?? 50;

  const response = await adminApi.get('/api/admin/mcp/calls', { params });
  return response.data.data;
};
